import {
  Card,
  CircularProgress,
  DoughnutChart,
  DownloadIcon,
  GKButton,
  GKDataGrid,
  GKSearchSelect,
  GKTypography,
  Grid2,
  IconButton,
  Show,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
} from "components";
import { usePortfolioSummary } from "hooks/usePortfolioSummary";
import { ModelPortfolioObjectInterface } from "interfaces/EquityBasket";
import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { updateFilter } from "store/slices/tableSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { numberToCurrency } from "utils/MathFunction";
import { assetsHoldingsColumns } from "utils/columns";
import { UserRoleTypes } from "utils/common";
import useDocumentTitle from "utils/useDocumentTitle";
import CardOne from "./CardOne";
import HoldingReportModal from "./HoldingReportModal";
import MultiLineComponent from "./MultiLineComponent";
import PortfolioChart from "./PortfolioChart";
import MainPortfolioChart from "./PortfolioSummaryMultiLineChart";

const HoldingsSummary = ({
  selectedModelPortfolio,
}: {
  selectedModelPortfolio?: ModelPortfolioObjectInterface;
}) => {
  useDocumentTitle("Holdings Overview");

  const dispatch = useAppDispatch();

  const userInfo = useAppSelector((state) => state?.user?.userData);

  const commonFilter = useAppSelector((state) => state?.table?.commonFilter);
  const partnerList = useAppSelector((state) => state?.app?.partnerList);

  const {
    setSelectedItems,
    selectedItems,
    selectedCriteria,
    setSelectedCriteria,
    lineChartData,
    handleRowClickApiCall,
    AUMData,
    loading,
    portfolioList,
    holdingData,
    linechartTabs,
    setLinechartTabs,
    id,
    pageScrollTabs,
    setpageScrollTabs,
    partnerSelection,
    setPartnerSelection,
    modelPortfolioId,
    downloadNavData,
    downloadLoading,
  } = usePortfolioSummary({ selectedModelPortfolio });

  const { pathname } = useLocation();

  const [isOpenModal, setIsOpenModal] = useState(false);

  const transformedData = useMemo(() => {
    if (!loading) {
      const benchmarkData =
        lineChartData?.data?.benchmarkComparison?.["sinceInception"];

      return benchmarkData
        ? Object.entries(benchmarkData).map(
            ([seriesName, seriesData], index) => {
              const typedData = seriesData as [number, number][];
              return {
                name: seriesName,
                data: typedData.map(([x, y]) => [x, y]),
                marker: { enabled: false },
                color: index === 0 ? "var(--primary-900)" : "#63a387",
                borderColor: index === 0 ? "var(--primary-900)" : "#63a387",
              };
            },
          )
        : [];
    }

    return [];
  }, [modelPortfolioId, id, loading]);

  return (
    <div>
      <Grid2 container spacing={2} pl={1}>
        <Show.When isTrue={!id}>
          <Grid2
            size={12}
            container
            spacing={2}
            display={"flex"}
            alignItems={"center"}
          >
            <Grid2 size={4}>
              <GKSearchSelect
                multiple
                disableClearable={false}
                limitTags={5}
                groupBy={(option: any) => option?.type?.toUpperCase()}
                getOptionLabel={(option) => option?.name || option}
                loading={loading}
                onChange={(event, val) => {
                  setSelectedItems(val);
                  setSelectedCriteria(`portfolioValue`);
                  dispatch(
                    updateFilter({
                      name: "holdingSummary",
                      filter: {
                        skip: 0,
                        page: 0,
                        take: 25,
                        sortModel: [],
                        search: "",
                        showNonZero: commonFilter.holdingSummary.showNonZero,
                        customFilter: {},
                      },
                    }),
                  );
                }}
                value={selectedItems}
                options={portfolioList || []}
                sx={{
                  "& .MuiInputBase-input.MuiOutlinedInput-input": {
                    padding: "1.5px !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    top: "-4px !important",
                  },
                }}
                renderOption={(props, option) => {
                  return (
                    <li
                      {...props}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        flexDirection: "column",
                        gap: 4,
                        background:
                          selectedItems?.filter(
                            (data) => data?.value === option?.value,
                          )?.length === 0
                            ? "var(--white)"
                            : `var(--primary-50)`,
                        color:
                          selectedItems?.filter(
                            (data) => data?.value === option?.value,
                          )?.length === 0
                            ? "var(--black)"
                            : "var(--primary-900)",
                      }}
                    >
                      <GKTypography fontWeight={600}>
                        {option.clientName}
                      </GKTypography>
                      <GKTypography>{option.name}</GKTypography>
                    </li>
                  );
                }}
                disableCloseOnSelect
                placeholder="Select ..."
              />
            </Grid2>
            <Grid2 size={4}>
              {userInfo?.userRole === UserRoleTypes.advisor && (
                <GKSearchSelect
                  disableClearable={false}
                  options={partnerList || []}
                  value={
                    partnerList?.filter((option: any) =>
                      partnerSelection.includes(option.value),
                    )[0]?.name || []
                  }
                  onChange={(event, val) => {
                    setPartnerSelection(val ? [val?.value] : []);
                  }}
                  getOptionLabel={(option) => option.name || option}
                  placeholder="Partner"
                />
              )}
            </Grid2>
            <Grid2
              size={4}
              container
              display={"flex"}
              gap={2}
              alignItems={"center"}
            >
              <Grid2
                width={"49%"}
                display={selectedItems?.length !== 1 ? "none" : "flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                gap={2}
                border={`solid 1px var(--neutral-100)`}
                px={2}
                py={1}
                borderRadius={"8px"}
              >
                <GKTypography
                  color={"var(--neutral-500)"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                  fontSize={12}
                  textAlign={"left"}
                >
                  XIRR
                </GKTypography>
                {loading ? (
                  <Skeleton animation="wave" width={"50%"} />
                ) : (
                  <GKTypography
                    display={"flex"}
                    alignItems={"center"}
                    fontWeight={700}
                    fontSize={16}
                    color={"var(--black)"}
                  >
                    {AUMData?.data?.xirr?.toFixed(2)}%
                  </GKTypography>
                )}
              </Grid2>
              <Grid2 ml={"auto"}>
                <Tabs
                  variant="standard"
                  className="light"
                  TabIndicatorProps={{ style: { display: "none" } }}
                  onChange={(e, val) => {
                    const anchor = (
                      (e.target as HTMLDivElement).ownerDocument || document
                    ).querySelector("#back-to-top-anchor");

                    if (anchor) {
                      anchor.scrollIntoView({
                        block: "center",
                        behavior: "smooth",
                      });
                      setpageScrollTabs(val);
                    }

                    setpageScrollTabs(0);
                  }}
                  value={pageScrollTabs}
                >
                  <Tab label="Overview" disableRipple value={0} />
                  <Tab label="Holdings" disableRipple value={1} />
                </Tabs>
              </Grid2>
            </Grid2>
          </Grid2>
        </Show.When>
        <Grid2 size={4} container spacing={2}>
          <Grid2 size={12}>
            <CardOne data={AUMData?.data} loading={loading} />
          </Grid2>
        </Grid2>
        <Grid2 size={4}>
          <Card sx={{ p: 0 }}>
            <Grid2 container>
              <Grid2
                size={12}
                p={2}
                borderBottom={`solid 1px var(--neutral-100)`}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                gap={1}
              >
                <GKTypography
                  fontSize={14}
                  fontWeight={600}
                  display={"flex"}
                  alignItems={"center"}
                  gap={0.5}
                  width={"50%"}
                >
                  Portfolio Value
                </GKTypography>
                <GKTypography fontSize={18} fontWeight={600} loading={loading}>
                  {numberToCurrency(AUMData?.data?.portfolioValue)}
                </GKTypography>
              </Grid2>
              <Grid2 size={12} px={1}>
                <DoughnutChart data={AUMData} loading={loading} />
              </Grid2>

              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                py={"0px"}
                px={"16px"}
                mb={2}
              >
                <GKTypography
                  display={"flex"}
                  alignItems={"center"}
                  fontWeight={400}
                  fontSize={12}
                  color={"var(--neutral-500)"}
                  width={"50%"}
                >
                  <span
                    style={{
                      height: 26,
                      width: 4,
                      borderRadius: "10px",
                      marginRight: 8,
                      background: "#FCAA67",
                    }}
                  />
                  All holdings
                </GKTypography>
                <GKTypography
                  fontWeight={600}
                  fontSize={12}
                  color={"#495567"}
                  loading={loading}
                >
                  {numberToCurrency(AUMData?.data?.currentValue)}
                </GKTypography>
              </Grid2>
              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                py={"0px"}
                px={"16px"}
                mb={2}
              >
                <GKTypography
                  display={"flex"}
                  alignItems={"center"}
                  fontWeight={400}
                  fontSize={12}
                  color={"var(--neutral-500)"}
                  width={"50%"}
                >
                  <span
                    style={{
                      height: 26,
                      width: 4,
                      borderRadius: "10px",
                      marginRight: 8,
                      background: "#39627A",
                    }}
                  />
                  Equity
                </GKTypography>
                <GKTypography
                  fontWeight={600}
                  fontSize={12}
                  color={"#495567"}
                  loading={loading}
                >
                  {numberToCurrency(AUMData?.data?.equity)}
                </GKTypography>
              </Grid2>
              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                py={"0px"}
                px={"16px"}
                mb={2}
              >
                <GKTypography
                  display={"flex"}
                  alignItems={"center"}
                  fontWeight={400}
                  fontSize={12}
                  width={"50%"}
                  color={"var(--neutral-500)"}
                >
                  <span
                    style={{
                      height: 26,
                      width: 4,
                      borderRadius: "10px",
                      marginRight: 8,
                      background: "#709D88",
                    }}
                  />
                  Ledger Balance
                </GKTypography>
                <GKTypography
                  fontWeight={600}
                  fontSize={12}
                  color={"#495567"}
                  loading={loading}
                >
                  {numberToCurrency(AUMData?.data?.ledgerBalance)}
                </GKTypography>
              </Grid2>
            </Grid2>
          </Card>
        </Grid2>
        <Grid2 size={4}>
          <Card sx={{ p: 0 }}>
            <Grid2 container>
              <Grid2
                size={12}
                p={2}
                borderBottom={`solid 1px var(--neutral-100)`}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                gap={1}
              >
                <GKTypography
                  fontSize={14}
                  fontWeight={600}
                  display={"flex"}
                  alignItems={"center"}
                  gap={0.5}
                  width={"50%"}
                >
                  Total Gain
                </GKTypography>
                <GKTypography fontSize={18} fontWeight={600} loading={loading}>
                  {numberToCurrency(AUMData?.data?.totalReturns)}
                </GKTypography>
              </Grid2>
              <Grid2 size={12} px={1}>
                <PortfolioChart data={AUMData?.data} loading={loading} />
              </Grid2>
              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                py={"0px"}
                mb={2}
                px={"16px"}
              >
                <GKTypography
                  display={"flex"}
                  alignItems={"center"}
                  fontWeight={400}
                  fontSize={12}
                  color={"var(--neutral-500)"}
                  width={"50%"}
                >
                  <span
                    style={{
                      height: 26,
                      width: 4,
                      borderRadius: "10px",
                      marginRight: 8,
                      background: "#39627A",
                    }}
                  />
                  Unrealised Gain
                </GKTypography>
                <GKTypography
                  fontWeight={600}
                  fontSize={12}
                  color={"#495567"}
                  loading={loading}
                >
                  {" "}
                  {numberToCurrency(AUMData?.data?.unrealisedGain)}
                </GKTypography>
              </Grid2>
              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                py={"0px"}
                mb={2}
                px={"16px"}
              >
                <GKTypography
                  display={"flex"}
                  alignItems={"center"}
                  fontWeight={400}
                  fontSize={12}
                  width={"50%"}
                  color={"var(--neutral-500)"}
                >
                  <span
                    style={{
                      height: 26,
                      width: 4,
                      borderRadius: "10px",
                      marginRight: 8,
                      background: "#709D88",
                    }}
                  />
                  Realised Gain{" "}
                </GKTypography>
                <GKTypography
                  fontWeight={600}
                  fontSize={12}
                  color={"#495567"}
                  loading={loading}
                >
                  {" "}
                  {numberToCurrency(AUMData?.data?.realisedGain)}
                </GKTypography>
              </Grid2>
              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                py={"0px"}
                px={"16px"}
                mb={2}
              >
                <GKTypography
                  display={"flex"}
                  alignItems={"center"}
                  fontWeight={400}
                  fontSize={12}
                  color={"var(--neutral-500)"}
                  width={"50%"}
                >
                  <span
                    style={{
                      height: 26,
                      width: 4,
                      borderRadius: "10px",
                      marginRight: 8,
                      background: "#FCAA67",
                    }}
                  />
                  Dividend
                </GKTypography>
                <GKTypography
                  fontWeight={600}
                  fontSize={12}
                  color={"#495567"}
                  loading={loading}
                >
                  {" "}
                  {numberToCurrency(AUMData?.data?.dividend)}
                </GKTypography>
              </Grid2>
            </Grid2>
          </Card>
        </Grid2>
        <Grid2 size={12}>
          <Card sx={{ p: 0 }}>
            <Grid2 container>
              <Grid2
                size={12}
                spacing={1}
                container
                alignItems={"center"}
                p={2}
                borderBottom={`solid 1px var(--neutral-100)`}
              >
                <Grid2 size={8}>
                  <GKTypography fontWeight={700} fontSize={18}>
                    Portfolio Curve
                  </GKTypography>
                </Grid2>
                <Grid2
                  size={4}
                  display={"flex"}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                  gap={1}
                >
                  <Tabs
                    className="light"
                    variant="standard"
                    TabIndicatorProps={{ style: { display: "none" } }}
                    value={linechartTabs}
                    onChange={(e, val) => {
                      setLinechartTabs(val);
                      setSelectedCriteria(val === 0 ? "portfolioValue" : "NAV");
                    }}
                  >
                    <Tab disableRipple label="Portfolio Value" value={0} />
                    <Tab
                      disableRipple
                      label={
                        pathname.includes("equity-basket")
                          ? "Performance"
                          : "NAV"
                      }
                      value={1}
                      disabled={selectedItems?.length === 0}
                    />
                  </Tabs>
                  <Show.When isTrue={!!pathname.includes("equity-basket")}>
                    <Tooltip title="Download NAV" arrow>
                      <IconButton
                        disabled={linechartTabs === 0 || downloadLoading}
                        onClick={() => downloadNavData()}
                        sx={{
                          bgcolor: "var(--primary-50)",
                          color: "var(--neutral-700)",
                          borderRadius: "4px",
                        }}
                      >
                        {downloadLoading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DownloadIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Show.When>
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                {selectedCriteria === "portfolioValue" && (
                  <MainPortfolioChart
                    data={lineChartData?.data?.data}
                    loading={loading}
                  />
                )}
                {selectedCriteria === "NAV" && (
                  <MultiLineComponent
                    multiLineData={
                      pathname.includes("equity-basket")
                        ? transformedData
                        : lineChartData?.data?.navs
                    }
                    isLoading={loading}
                  />
                )}
              </Grid2>
            </Grid2>
          </Card>
        </Grid2>
        <Show.When isTrue={!id}>
          <Grid2 size={12}>
            <GKDataGrid
              density="compact"
              loading={loading}
              minHeight={"calc(100vh - 256px)"}
              columns={assetsHoldingsColumns}
              rows={holdingData?.data?.data || []}
              maxHeight={"calc(100vh - 318px)"}
              zeroHolding
              totalCount={holdingData?.data?.totalCount || 0}
              name="holdingSummary"
              tableName="holding_summary_list"
              onRowClick={(row) => {
                handleRowClickApiCall(row?.row?.gcode);
              }}
              pinnedColumns={{ left: ["name"] }}
              headerComponent={
                <GKButton
                  variant="contained"
                  size="medium"
                  onClick={() => setIsOpenModal(true)}
                >
                  Holding Report
                </GKButton>
              }
              exportFile={{
                path: `/holdings/selected/`,
                exportName: "HoldingsList",
              }}
            />
          </Grid2>

          <Show.When isTrue={isOpenModal}>
            <HoldingReportModal open={isOpenModal} setOpen={setIsOpenModal} />
          </Show.When>
        </Show.When>
        {!id && <div id={"back-to-top-anchor"} />}
      </Grid2>
    </div>
  );
};

export default HoldingsSummary;
